// Marco de la consola: el mismo menú lateral del resto de Glubi. Abajo, los negocios de quien entra: el
// administrador ve el suyo, un implementador todos los que atiende y cambia entre ellos sin salir.
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Icon, type NombreIcono } from '@glubi/ds';
import logotipo from '@glubi/ds/assets/glubi-logotipo-inverso.svg';
import { api } from '../lib/api';
import { useSesion } from '../lib/sesion';

const TITULO: Record<string, string> = {
  '/resumen': 'Resumen', '/probador': 'Probador', '/modelos': 'Modelos', '/conectores': 'Conectores', '/credenciales': 'Credenciales',
  '/llaves': 'Llaves', '/registro': 'Registro de llamadas', '/negocios': 'Mis negocios', '/tablero': 'Tablero', '/agentes': 'Agentes', '/rutinas': 'Rutinas',
};

const ROL: Record<string, string> = { administrador: 'Administrador', implementador: 'Implementador', administracion: 'Administración' };

export function Marco({ children }: { children: ReactNode }) {
  const { yo, cuenta, elegirCuenta, salir } = useSesion();
  const [menu, setMenu] = useState(false);
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  const conectores = useQuery({ queryKey: ['conectores'], queryFn: () => api<any[]>('/api/conectores'), refetchInterval: 120_000 });
  const conError = (conectores.data ?? []).filter((c) => c.estado === 'activo' && c.ultimo_error).length;
  const tablero = useQuery({ queryKey: ['tablero'], queryFn: () => api<{ porAprobar: unknown[] }>('/api/tablero'), refetchInterval: 30_000 });
  const porAprobar = tablero.data?.porAprobar.length ?? 0;

  useEffect(() => setMenu(false), [ruta]);
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  const item = (to: string, label: string, icon: NombreIcono, extra?: { count?: number; alerta?: boolean }) => {
    const activo = ruta === to;
    return (
      <Link key={to} to={to} className={`nav-item ${activo ? 'active' : ''} ${extra?.alerta ? 'quarantine' : ''}`} aria-current={activo ? 'page' : undefined} style={{ textDecoration: 'none' }}>
        <Icon name={icon} size={16} color={extra?.alerta ? 'var(--amber)' : 'var(--paper)'} />
        {label}
        {Boolean(extra?.count) && <span className="nav-count">{extra!.count}</span>}
      </Link>
    );
  };
  const grupo = (hijos: ReactNode) => <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{hijos}</div>;

  return (
    <div className="shell">
      <header className="topbar">
        <button className="menu-btn" aria-label="Abrir el menú" aria-expanded={menu} aria-controls="menu-panel" onClick={() => setMenu(true)}>
          <Icon name="menu" size={20} />
        </button>
        <img src={logotipo} alt="Glubi" />
        <span className="where">{cuenta?.cuenta} · {TITULO[ruta] ?? 'Gateway'}</span>
      </header>
      <div className={`drawer-scrim ${menu ? 'open' : ''}`} onClick={() => setMenu(false)} />
      <nav id="menu-panel" className={`sidebar ${menu ? 'open' : ''}`} aria-label="Navegación del gateway">
        <img src={logotipo} alt="Glubi" className="sidebar-logo" />
        <div className="nav-eyebrow" style={{ marginTop: 0 }}>Gateway de IA y MCP</div>
        {grupo(<>
          {item('/resumen', 'Resumen', 'signal')}
          {item('/probador', 'Probador', 'message-square')}
        </>)}
        <div className="nav-eyebrow">Agentes</div>
        {grupo(<>
          {item('/tablero', 'Tablero', 'tablero', { count: porAprobar, alerta: porAprobar > 0 })}
          {item('/agentes', 'Agentes', 'bot')}
          {item('/rutinas', 'Rutinas', 'workflow')}
        </>)}
        <div className="nav-eyebrow">Conexiones</div>
        {grupo(<>
          {item('/modelos', 'Modelos', 'flask-conical')}
          {item('/conectores', 'Conectores', 'plug', { count: conError, alerta: conError > 0 })}
          {item('/credenciales', 'Credenciales', 'lock')}
        </>)}
        <div className="nav-eyebrow">Acceso</div>
        {grupo(<>
          {item('/llaves', 'Llaves', 'key')}
          {item('/registro', 'Registro de llamadas', 'history')}
        </>)}
        {(yo?.cuentas.length ?? 0) > 1 && <>
          <div className="nav-eyebrow">Implementación</div>
          {grupo(item('/negocios', 'Mis negocios', 'users'))}
        </>}
        <div style={{ marginTop: 'auto', paddingTop: 20 }}>
          <div className="biz-switch">
            {yo?.cuentas.map((c) => (
              <button key={c.cuenta_id} className={`biz-option ${c.cuenta_id === cuenta?.cuenta_id ? 'active' : ''}`} onClick={() => elegirCuenta(c.cuenta_id)}>
                {c.cuenta}
                <small>{ROL[c.rol]}{c.puedeAdministrar ? '' : ' · solo ver'}</small>
              </button>
            ))}
            <button className="biz-option" onClick={() => void salir()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="log-out" size={12} /> Salir
            </button>
          </div>
        </div>
      </nav>
      <main className="main">{children}</main>
    </div>
  );
}
